import {
	CancelTwoTone as CancelIcon,
	CheckCircleTwoTone as CheckCircleIcon,
	Delete as DeleteIcon,
	Edit as EditIcon,
	ExpandMore as ExpandMoreIcon,
	ReportGmailerrorred as SuspendIcon,
} from '@mui/icons-material';
import {
	Box,
	Chip,
	Collapse,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { WebhookResponse } from '../../api/webhooks';
import {
	WEBHOOKS_CONFIGURATIONS_TABLE_COLLAPSE_ACT_ON_STATUS_HEADER,
	WEBHOOKS_CONFIGURATIONS_TABLE_COLLAPSE_ACT_ON_TIMEOUT_HEADER,
	WEBHOOKS_CONFIGURATIONS_TABLE_COLLAPSE_ALERT_INTEGRATION_HEADER,
	WEBHOOKS_CONFIGURATIONS_TABLE_COLLAPSE_EVENTS_HEADER,
	WEBHOOKS_CONFIGURATIONS_TABLE_COLLAPSE_FAILURE_MODE_HEADER,
	WEBHOOKS_CONFIGURATIONS_TABLE_COLLAPSE_RETRY_STRATEGY_HEADER,
	WEBHOOKS_CONFIGURATIONS_TABLE_INTEGRATION_HEADER_LABEL,
	WEBHOOKS_CONFIGURATIONS_TABLE_NAME_HEADER_LABEL,
	WEBHOOKS_CONFIGURATIONS_TABLE_STATUS_ACTIONS_LABEL,
	WEBHOOKS_CONFIGURATIONS_TABLE_STATUS_ENABLED,
	WEBHOOKS_CONFIGURATIONS_TABLE_SUSPENDED_HEADER_LABEL,
} from '../../data/constants';
import ConfirmDeleteWebhookConfigurationModal from './modals/ConfirmDeleteWebhookConfigurationModal';
import ConfirmUnsuspendWebhookConfigurationModal from './modals/ConfirmUnsuspendWebhookConfigurationModal';

type Props = {
	configuredWebhooks: WebhookResponse[];
	tenantHostname?: string;
	onEdit: (configuredWebhook: WebhookResponse) => void;
};

type TableHeader = {
	label: string;
	align: 'center' | 'inherit' | 'left';
};

type ExpandInfoState = {
	[webhookName: string]: boolean;
};

type ConfirmConfigurationModalState = {
	show: boolean;
	configuredWebhook: WebhookResponse;
};

// TODO: implement skeleton while loading
export default function WebhooksConfigurationsTable({
	configuredWebhooks,
	tenantHostname,
	onEdit,
}: Props) {
	const [expandInfo, setExpandInfo] = useState<ExpandInfoState>({});
	const [
		showWebhookDeleteConfigurationModalInfo,
		setShowWebhookDeleteConfigurationModalInfo,
	] = useState<ConfirmConfigurationModalState>({
		show: false,
		configuredWebhook: null,
	});
	const [
		showWebhookUnsuspendConfigurationModalInfo,
		setShowWebhookUnsuspendConfigurationModalInfo,
	] = useState<ConfirmConfigurationModalState>({
		show: false,
		configuredWebhook: null,
	});

	const tableHeaders: TableHeader[] = [
		{
			label: '',
			align: 'inherit',
		},
		{
			label: WEBHOOKS_CONFIGURATIONS_TABLE_NAME_HEADER_LABEL(),
			align: 'left',
		},
		{
			label: WEBHOOKS_CONFIGURATIONS_TABLE_INTEGRATION_HEADER_LABEL(),
			align: 'left',
		},
		{
			label: WEBHOOKS_CONFIGURATIONS_TABLE_SUSPENDED_HEADER_LABEL(),
			align: 'center',
		},
		{
			label: WEBHOOKS_CONFIGURATIONS_TABLE_STATUS_ENABLED(),
			align: 'center',
		},
		{
			label: WEBHOOKS_CONFIGURATIONS_TABLE_STATUS_ACTIONS_LABEL(),
			align: 'center',
		},
	];
	const renderTableHeader = () => {
		return (
			<TableHead id='WebhooksPanel__WebhooksConfigurationTableHead'>
				<TableRow id='WebhooksPanel__WebhooksConfigurationTableHeadRow'>
					{tableHeaders.map((header, index) => (
						<TableCell
							key={`WebhooksConfigurationTable__TableHeaderCell-${index}`}
							align={header.align}
							sx={{ backgroundColor: 'white.lightest' }}
						>
							<Typography fontWeight='bold'>{header.label}</Typography>
						</TableCell>
					))}
				</TableRow>
			</TableHead>
		);
	};

	const getStatusIcon = (enabled: boolean) => {
		return enabled ? (
			<CheckCircleIcon
				sx={{
					color: 'success.main',
				}}
			/>
		) : (
			<CancelIcon
				sx={{
					color: 'black.main',
				}}
			/>
		);
	};

	const renderCollapseSection = (configuredWebhook: WebhookResponse) => {
		const events = configuredWebhook?.events;
		const alertIntegrationName = configuredWebhook?.alertIntegrationName;
		const retryStrategy = configuredWebhook?.retryStrategy;
		const failureHandling = configuredWebhook?.failureHandling;
		return (
			<Stack direction='column' spacing={1} paddingBottom={2}>
				<Typography variant='bodySemiBold'>
					{WEBHOOKS_CONFIGURATIONS_TABLE_COLLAPSE_EVENTS_HEADER(
						configuredWebhook?.events?.length ?? 0,
					)}
				</Typography>
				{events?.length ? (
					<Box marginTop={1}>
						{/* sort events alphabetically */}
						{configuredWebhook.events.sort().map((event) => (
							<Chip
								key={event}
								label={event}
								sx={{
									marginRight: 0.5,
									marginBottom: 1,
								}}
							/>
						))}
					</Box>
				) : null}
				{alertIntegrationName && (
					<>
						<Typography variant='bodySemiBold'>
							{WEBHOOKS_CONFIGURATIONS_TABLE_COLLAPSE_ALERT_INTEGRATION_HEADER()}
						</Typography>
						<Typography marginTop={1}>
							{configuredWebhook.alertIntegrationName}
						</Typography>
					</>
				)}
				{retryStrategy && (
					<>
						<Typography variant='bodySemiBold'>
							{WEBHOOKS_CONFIGURATIONS_TABLE_COLLAPSE_RETRY_STRATEGY_HEADER()}
						</Typography>
						<Typography marginTop={1}>
							{configuredWebhook.retryStrategy}
						</Typography>
					</>
				)}
				{retryStrategy !== 'none' && failureHandling?.mode && (
					<>
						<Typography variant='bodySemiBold'>
							{WEBHOOKS_CONFIGURATIONS_TABLE_COLLAPSE_FAILURE_MODE_HEADER()}
						</Typography>
						<Typography marginTop={1}>
							{configuredWebhook.failureHandling.mode}
						</Typography>
					</>
				)}
				{retryStrategy !== 'none' && failureHandling?.actOnStatusCodes ? (
					<>
						<Typography variant='bodySemiBold'>
							{WEBHOOKS_CONFIGURATIONS_TABLE_COLLAPSE_ACT_ON_STATUS_HEADER()}
						</Typography>
						<Box marginTop={1}>
							{/* sort codes numerically */}
							{failureHandling.actOnStatusCodes.length ? (
								failureHandling.actOnStatusCodes.sort().map((status) => (
									<Chip
										key={status}
										label={status}
										sx={{
											marginRight: 0.5,
											marginBottom: 1,
										}}
									/>
								))
							) : (
								<>
									<Chip
										key='4xx'
										label='4xx'
										sx={{
											marginRight: 0.5,
											marginBottom: 1,
										}}
									/>
									<Chip
										key='5xx'
										label='5xx'
										sx={{
											marginRight: 0.5,
											marginBottom: 1,
										}}
									/>
								</>
							)}
						</Box>
					</>
				) : null}
				{retryStrategy !== 'none' && failureHandling && (
					<>
						<Typography variant='bodySemiBold'>
							{WEBHOOKS_CONFIGURATIONS_TABLE_COLLAPSE_ACT_ON_TIMEOUT_HEADER()}
						</Typography>
						<Typography marginTop={1}>
							{configuredWebhook.failureHandling.actOnTimeout ? 'Yes' : 'No'}
						</Typography>
					</>
				)}
			</Stack>
		);
	};

	const renderTableBody = () => {
		return (
			<TableBody id='WebhooksPanel__WebhooksConfigurationTableBody'>
				{configuredWebhooks?.map((configuredWebhook) => (
					<React.Fragment key={configuredWebhook.name}>
						<TableRow
							sx={{
								'& td': { border: 0, paddingY: 0 },
							}}
						>
							<TableCell
								align='center'
								sx={{
									paddingX: 1,
									paddingY: 0,
								}}
							>
								<IconButton
									onClick={() => {
										setExpandInfo({
											...expandInfo,
											[configuredWebhook.name]: !(
												expandInfo[configuredWebhook.name] ?? false
											),
										});
									}}
									id={`WebhooksConfigurationTableBody__${configuredWebhook.name}-CollapseButton`}
								>
									<ExpandMoreIcon
										sx={{
											transform: expandInfo[configuredWebhook.name]
												? 'rotate(180deg)'
												: 'rotate(0deg)',
											transition:
												'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
										}}
									/>
								</IconButton>
							</TableCell>
							<TableCell>{configuredWebhook.name}</TableCell>
							<TableCell>{configuredWebhook.integrationName}</TableCell>
							<TableCell align='center'>
								{configuredWebhook.suspended && (
									<IconButton
										onClick={() => {
											setShowWebhookUnsuspendConfigurationModalInfo({
												show: true,
												configuredWebhook,
											});
										}}
									>
										<SuspendIcon color='warning' />
									</IconButton>
								)}
							</TableCell>
							<TableCell align='center'>
								{getStatusIcon(configuredWebhook.enabled)}
							</TableCell>
							<TableCell align='center'>
								<IconButton
									color='primary'
									onClick={() => onEdit(configuredWebhook)}
								>
									<EditIcon />
								</IconButton>
								<IconButton
									color='primary'
									onClick={() => {
										setShowWebhookDeleteConfigurationModalInfo({
											show: true,
											configuredWebhook,
										});
									}}
								>
									<DeleteIcon />
								</IconButton>
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell
								colSpan={tableHeaders.length}
								sx={{
									paddingTop: 0,
									paddingBottom: 0,
								}}
							>
								<Collapse
									in={expandInfo[configuredWebhook.name]}
									timeout='auto'
									unmountOnExit
								>
									{renderCollapseSection(configuredWebhook)}
								</Collapse>
							</TableCell>
						</TableRow>
					</React.Fragment>
				))}
			</TableBody>
		);
	};
	return (
		<>
			<Paper sx={{ flex: 1, overflowY: 'auto', borderRadius: 2 }}>
				<TableContainer
					sx={{ maxHeight: '100%' }}
					id='WebhooksPanel__WebhooksConfigurationTableContainer'
				>
					<Table
						stickyHeader
						id='WebhooksPanel__WebhooksConfigurationTable'
						size='small'
					>
						{renderTableHeader()}
						{renderTableBody()}
					</Table>
				</TableContainer>
			</Paper>
			{showWebhookDeleteConfigurationModalInfo.show && (
				<ConfirmDeleteWebhookConfigurationModal
					open={showWebhookDeleteConfigurationModalInfo.show}
					configuredWebhook={
						showWebhookDeleteConfigurationModalInfo.configuredWebhook
					}
					onCancel={() => {
						setShowWebhookDeleteConfigurationModalInfo({
							show: false,
							configuredWebhook: null,
						});
					}}
					tenantHostname={tenantHostname}
				/>
			)}
			{showWebhookUnsuspendConfigurationModalInfo.show && (
				<ConfirmUnsuspendWebhookConfigurationModal
					open={showWebhookUnsuspendConfigurationModalInfo.show}
					tenantHostname={tenantHostname}
					configuredWebhook={
						showWebhookUnsuspendConfigurationModalInfo.configuredWebhook
					}
					onCancel={() => {
						setShowWebhookUnsuspendConfigurationModalInfo({
							show: false,
							configuredWebhook: null,
						});
					}}
				/>
			)}
		</>
	);
}
